import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import AppShell from '../components/layout/AppShell';
import { authAPI, progressAPI } from '../utils/api';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import DailyReminder from '../components/DailyReminder';
import DailyVerseCard from '../components/DailyVerseCard';
import { Flame, Clock, Sparkles, ArrowRight, Calendar, Heart, Target, Book, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const HERO_BG = 'https://images.pexels.com/photos/13549654/pexels-photo-13549654.jpeg';

const useQuickActions = (lang) => ([
  { to: '/oku', label: lang === 'en' ? 'Read Quran' : 'Kuran Oku', desc: lang === 'en' ? 'Read surahs' : 'Sureleri oku', icon: Book, color: 'teal' },
  { to: '/dinle', label: lang === 'en' ? 'Listen Quran' : 'Kuran Dinle', desc: lang === 'en' ? 'Listen recitation' : 'Kıraat dinle', icon: BookOpen, color: 'cyan' },
  { to: '/hatim', label: lang === 'en' ? 'Hatim' : 'Hatim', desc: lang === 'en' ? 'Juz tracker' : 'Cüz takibi', icon: Target, color: 'emerald' },
  { to: '/dualar', label: lang === 'en' ? 'Special Prayers' : 'Özel Dualar', desc: lang === 'en' ? 'Daily duas' : 'Günlük dualar', icon: Heart, color: 'rose' },
]);

const Home = () => {
  const { isKidsMode } = useMode();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [lastPractice, setLastPractice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [u, s, p] = await Promise.all([
          authAPI.getMe(),
          progressAPI.getStats(),
          progressAPI.getUserProgress(),
        ]);
        setUser(u.data);
        setStats(s.data);
        setLastPractice(p.data?.last_practice || null);
      } catch (e) {
        toast.error('Veriler yüklenemedi');
      } finally {
        setLoading(false);
      }
    })();
    const id = setInterval(() => {
      import('../utils/api').then(({ default: api }) => api.post('/user/heartbeat').catch(() => {}));
    }, 60000);
    return () => clearInterval(id);
  }, []);

  const accent = isKidsMode ? 'amber' : 'teal';
  const heroOverlay = isKidsMode
    ? 'bg-gradient-to-r from-amber-900/90 via-amber-800/70 to-amber-700/40'
    : 'bg-gradient-to-r from-teal-950/90 via-teal-900/70 to-teal-800/30';

  const continueText = lastPractice
    ? `${lastPractice.surah_name || 'Kuran'} ${lastPractice.ayah_number ? '— Ayet ' + lastPractice.ayah_number : ''}`
    : 'Yeni bir yolculuk başlat';

  return (
    <AppShell user={user} title="Anasayfa">
      <div className="max-w-7xl mx-auto p-4 sm:p-8 md:p-12 space-y-8">
        <EmailVerificationBanner user={user} />
        {/* Combined greeting + Daily Verse — placed at the very top per request */}
        <DailyVerseCard greetingName={user?.name || ''} />
        <DailyReminder />

        {/* Continue card */}
        <section
          data-testid="hero-continue-card"
          className="relative overflow-hidden rounded-3xl border border-stone-200 shadow-sm bg-white p-6 sm:p-8"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('home.continue_from')}
          </p>
          <h2
            className="text-2xl sm:text-3xl font-bold mb-3 tracking-tight text-stone-900"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {continueText}
          </h2>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigate(isKidsMode ? '/elifba' : '/hafizlik')}
              data-testid="hero-continue-btn"
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-colors shadow-sm ${isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800'}`}
            >
              {isKidsMode ? (lang === 'tr' ? 'Elif-Ba\'ya Devam' : 'Continue Elif-Ba') : (lang === 'tr' ? 'Hafızlığa Devam' : 'Continue Hifz')}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/dinle')}
              data-testid="hero-listen-btn"
              className="inline-flex items-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-800 px-6 py-3 rounded-xl font-semibold transition-colors"
            >
              {lang === 'tr' ? 'Kuran Dinle' : 'Listen to Quran'}
            </button>
          </div>
        </section>

        {/* Stats Row */}
        <section className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="stats-row">
          <StatCard
            icon={Flame}
            label={t('home.stat.streak')}
            value={`${stats?.streak_days || 0} ${t('common.units_day')}`}
            accent={accent}
          />
          <StatCard
            icon={Clock}
            label={t('home.stat.minutes')}
            value={`${stats?.total_minutes || 0} ${t('common.units_min')}`}
            accent={accent}
          />
          <StatCard
            icon={Sparkles}
            label={t('home.stat.memorized')}
            value={`${stats?.memorized_ayahs || 0}`}
            accent={accent}
          />
        </section>

        {/* Quick action cards */}
        <section data-testid="quick-actions">
          <h2 className="text-2xl font-bold text-stone-800 mb-4 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('home.today_question')}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {useQuickActions(lang).map((qa) => (
              <button
                type="button"
                key={qa.to}
                onClick={() => navigate(qa.to)}
                data-testid={`quick-action-${qa.to.slice(1)}`}
                className="group text-left p-6 bg-white rounded-2xl border border-stone-200 hover:border-stone-300 hover:shadow-md transition-all"
              >
                <div className={`w-12 h-12 rounded-xl ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-700'} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                  <qa.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {qa.label}
                </h3>
                <p className="text-sm text-stone-500">{qa.desc}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Bottom widgets row */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <HatimWidget accent={accent} onClick={() => navigate('/hatim')} lang={lang} />
          <ReligiousCalendarWidget accent={accent} onClick={() => navigate('/dini-gunler')} lang={lang} />
          <DailyHadithWidget accent={accent} onClick={() => navigate('/hadisler')} lang={lang} />
        </section>

        <footer className="text-center text-xs text-stone-400 pt-6">
          Hafızım © {new Date().getFullYear()} — Kuran ile bir yolculuk
        </footer>
      </div>
    </AppShell>
  );
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-2xl p-5 border border-stone-200 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3 mb-2">
      <Icon className={`w-5 h-5 ${accent === 'amber' ? 'text-amber-600' : 'text-teal-700'}`} />
      <span className="text-xs uppercase tracking-[0.2em] text-stone-500">{label}</span>
    </div>
    <div className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {value}
    </div>
  </div>
);

const HatimWidget = ({ accent, onClick, lang }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="widget-hatim"
    className="text-left bg-white rounded-2xl p-6 border border-stone-200 hover:border-stone-300 hover:shadow-md transition-all"
  >
    <div className="flex items-center gap-3 mb-4">
      <Target className={`w-5 h-5 ${accent === 'amber' ? 'text-amber-600' : 'text-teal-700'}`} />
      <span className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {lang === 'en' ? 'Hatim Progress' : 'Hatim İlerlemen'}
      </span>
    </div>
    <div className="flex items-end justify-between mb-3">
      <div className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>0 / 30</div>
      <span className="text-xs text-stone-500">{lang === 'en' ? 'juz' : 'cüz'}</span>
    </div>
    <div className="w-full bg-stone-100 rounded-full h-2 overflow-hidden">
      <div className={`${accent === 'amber' ? 'bg-amber-500' : 'bg-teal-600'} h-full transition-all`} style={{ width: '0%' }} />
    </div>
    <p className="text-xs text-stone-500 mt-3">{lang === 'en' ? 'Tap to start Hatim' : 'Hatime başlamak için tıkla'}</p>
  </button>
);

const ReligiousCalendarWidget = ({ accent, onClick, lang }) => {
  const today = new Date();
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="widget-religious-calendar"
      className="text-left bg-white rounded-2xl p-6 border border-stone-200 hover:border-stone-300 hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-3 mb-4">
        <Calendar className={`w-5 h-5 ${accent === 'amber' ? 'text-amber-600' : 'text-teal-700'}`} />
        <span className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {lang === 'en' ? 'Religious Calendar' : 'Dini Takvim'}
        </span>
      </div>
      <div className="text-2xl font-bold text-stone-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {today.toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', { day: 'numeric', month: 'long' })}
      </div>
      <p className="text-xs text-stone-500">{lang === 'en' ? "Tap for today's worship" : 'Bugünün ibadetleri için tıkla'}</p>
    </button>
  );
};

const DailyHadithWidget = ({ accent, onClick, lang }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="widget-daily-hadith"
    className="text-left bg-white rounded-2xl p-6 border border-stone-200 hover:border-stone-300 hover:shadow-md transition-all"
  >
    <div className="flex items-center gap-3 mb-4">
      <Sparkles className={`w-5 h-5 ${accent === 'amber' ? 'text-amber-600' : 'text-teal-700'}`} />
      <span className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {lang === 'en' ? 'Hadith of the Day' : 'Günün Hadisi'}
      </span>
    </div>
    <p className="text-sm text-stone-700 italic leading-relaxed mb-2">
      &ldquo;{lang === 'en'
        ? 'The best of you are those who learn the Quran and teach it.'
        : "Sizin en hayırlınız, Kur'an'ı öğrenen ve öğretendir."}&rdquo;
    </p>
    <p className="text-xs text-stone-400">— Buhârî, Fedâilü&apos;l-Kur&apos;ân, 21</p>
  </button>
);

export default Home;
